import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  LinearProgress,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ShowChart,
  Speed,
  EventNote,
  AttachMoney,
  VolunteerActivism,
  Campaign,
} from '@mui/icons-material';
import Chart from '../../../../components/Admin/Common/Chart';
import { Metrics } from '../../../../types/metrics';
import { ActivityItem, GoalItem } from '../hooks/useDashboardData';

interface MainContentProps {
  currentTab: number;
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  metrics: Metrics | null;
  performanceGoals: GoalItem[];
  activityItems: ActivityItem[];
}

const MainContent: React.FC<MainContentProps> = ({
  currentTab,
  onTabChange,
  metrics,
  performanceGoals,
  activityItems,
}) => {
  const theme = useTheme();

  return (
    <Paper 
      sx={{ 
        p: 0, 
        mb: 3, 
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: theme.shadows[3],
      }}
    >
      <Tabs 
        value={currentTab} 
        onChange={onTabChange}
        sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          px: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.04)
        }}
      >
        <Tab 
          label="Overview" 
          icon={<ShowChart />} 
          iconPosition="start" 
        />
        <Tab 
          label="Performance" 
          icon={<Speed />} 
          iconPosition="start" 
        />
        <Tab 
          label="Activity" 
          icon={<EventNote />} 
          iconPosition="start" 
        />
      </Tabs>

      <Box sx={{ p: 3 }}>
        {/* Overview Tab */}
        {currentTab === 0 && (
          <>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Donation Trends
            </Typography>
            {metrics?.donationTrends && metrics.donationTrends.length > 0 ? (
              <Chart
                data={metrics.donationTrends}
                title="Monthly Donations"
              />
            ) : (
              <Typography sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                No donation trends data available.
              </Typography>
            )}
          </>
        )}

        {/* Performance Tab */}
        {currentTab === 1 && (
          <>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Current Goals Progress
            </Typography>
            
            <Box sx={{ mb: 4 }}>
              {performanceGoals.map((item, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" fontWeight={500}>
                      {item.name}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {item.unit}{item.current.toLocaleString()} / {item.unit}{item.target.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ flexGrow: 1, mr: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={(item.current / item.target) * 100} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 5,
                          bgcolor: alpha(theme.palette.primary.main, 0.15),
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 5,
                          }
                        }} 
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {Math.round((item.current / item.target) * 100)}%
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button 
                variant="outlined" 
                color="primary"
              >
                Set New Goals
              </Button>
            </Box>
          </>
        )}

        {/* Activity Tab */}
        {currentTab === 2 && (
          <>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Recent Activity
            </Typography>
            
            {activityItems.length === 0 ? (
              <Typography sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                No recent activity available.
              </Typography>
            ) : (
              <List sx={{ width: '100%' }}>
                {activityItems.map((activity) => (
                  <ListItem 
                    key={activity.id}
                    alignItems="flex-start"
                    sx={{ 
                      px: 2, 
                      py: 1.5,
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ 
                        bgcolor: 
                          activity.type === 'donation' ? alpha(theme.palette.info.main, 0.1) :
                          activity.type === 'volunteer' ? alpha(theme.palette.success.main, 0.1) :
                          alpha(theme.palette.warning.main, 0.1),
                        color:
                          activity.type === 'donation' ? theme.palette.info.main :
                          activity.type === 'volunteer' ? theme.palette.success.main :
                          theme.palette.warning.main,
                      }}>
                        {activity.type === 'donation' ? <AttachMoney /> :
                         activity.type === 'volunteer' ? <VolunteerActivism /> :
                         <Campaign />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography fontWeight={500}>
                          {activity.name} {activity.action} {activity.amount && `(${activity.amount})`}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {activity.timestamp}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};

export default MainContent; 